import { gql } from '@apollo/client'

export const TICKET_CONTEXT_CAPABILITIES_QUERY = gql`
  query TicketContextCapabilities {
    ticketContextCapabilities {
      enabledRefTypes
      maxAffectedEntities
      maxContextHints
    }
  }
`

/**
 * Support ticketing (ADR-017 v0.7, Feature 007). Backed by ticket-service
 * through npanel-bff, which is the sole ingress (ADR-030).
 *
 * Ticket ownership is explicit reporter/affected entity identity. Container
 * membership is a read-time projection and never authorization input.
 *
 * VISIBILITY is per row, not per endpoint: a ticket the caller may not see
 * returns NOT FOUND, not FORBIDDEN. "Exists but hidden" and "does not exist"
 * must stay indistinguishable, so do not add UI that reports them differently.
 */

export const TICKET_FIELDS = gql`
  fragment TicketFields on Ticket {
    __typename
    id
    ticketNumber
    reporter {
      type
      userId
      displayName
      entityType
      entityCode
      serviceCode
    }
    reporterEntityRef {
      type
      code
    }
    containerRefs {
      type
      code
    }
    containerResolutionStatus
    createdBy {
      kind
      principalId
    }
    intakeChannel
    assigneeUserId
    assigneeIsAdotelAdmin
    watcherUserIds
    affectedEntityRefs {
      type
      code
    }
    state
    priority
    submissionChannel
    productScope
    sourcePageKey
    relatedRecords {
      id
      refType
      recordId
      distributionRef {
        propertyCode
        roomTypeCode
        ratePlanCode
        channelCode
      }
      entityRef {
        type
        code
      }
      status
      snapshotVersion
      capturedAt
    }
    title
    version
    createdAt
    changedAt
    hasDiagnosticContext
    sla {
      policyId
      policyVersion
      respondBy
      resolveBy
      breachedAt
      pausedSeconds
    }
  }
`

export const TICKETS_QUERY = gql`
  ${TICKET_FIELDS}
  query Tickets(
    $tab: TicketTab
    $search: String
    $pageSize: Int
    $pageToken: String
  ) {
    tickets(
      tab: $tab
      search: $search
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      tickets {
        ...TicketFields
      }
      nextPageToken
    }
  }
`

/**
 * The badge's own query: the SAME `tickets` field, with only the two fields a
 * count actually reads.
 *
 * TICKETS_QUERY spreads TicketFields — ~30 leaf fields plus reporter, sla,
 * containerRefs, watcherUserIds and affectedEntityRefs — because it feeds the
 * ticket TABLE. Reusing it to compute a number pulled every one of those for 50
 * tickets on the first authenticated render of every session, for every user,
 * whether or not they ever open Support.
 *
 * This is not the honest fix. That is a count field on the BFF, which would
 * also retire the "at least N" approximation instead of just making it cheaper
 * to compute — see NotificationBell's NOTIFICATION_UNREAD_COUNT_QUERY for the
 * shape. Until that exists, this needs no server change and drops the selection
 * set to two scalars.
 */
export const TICKET_BADGE_COUNT_QUERY = gql`
  query TicketBadgeCount($tab: TicketTab, $pageSize: Int) {
    tickets(tab: $tab, pageSize: $pageSize) {
      tickets {
        id
        state
      }
      nextPageToken
    }
  }
`

/**
 * Fields only the DETAIL screen reads, kept out of `TicketFields` on purpose.
 *
 * `TicketFields` is spread by `TICKETS_QUERY` for the ticket TABLE, so anything
 * added there is fetched for every row of every page — up to 50 tickets — even
 * though the table renders none of it. That is the same waste
 * `TICKET_BADGE_COUNT_QUERY` exists to avoid, and it is why these five policy
 * dimensions and two durations live here instead: the Service level card is the
 * only thing that reads them.
 *
 * The `sla` selection MERGES with the one in `TicketFields` rather than
 * replacing it — GraphQL unions the two field sets on the same type — so this
 * adds to the deadlines the fragment already selects.
 */
export const TICKET_DETAIL_FIELDS = gql`
  fragment TicketDetailFields on Ticket {
    sla {
      # Zero means "not published", NOT a promise of zero seconds: ticket-service
      # joins an immutable policy version at read time, and a pruned version
      # yields nothing — which proto3 scalars cannot distinguish from a real
      # zero. The panel treats 0 as absent.
      respondAfterSeconds
      resolveAfterSeconds
      # What the policy IS, since it has no name. '*' in the entity code is the
      # platform default; a concrete code is that customer's override.
      policyReporterEntityCode
      policyReporterEntityType
      policyProductScope
      policyPriority
      policyIntakeChannel
    }
    reporter {
      # The organization's NAME, resolved by the BFF against the assets
      # directory at read time. Detail-only for the same reason as the SLA
      # dimensions above: no ticket table renders a reporter, so a 50-row page
      # would pay for a directory lookup nothing shows. Absent when the
      # directory cannot answer, or for a reporter type it does not hold —
      # entityCode remains in TicketFields as the fallback.
      entityName
    }
  }
`

export const TICKET_QUERY = gql`
  ${TICKET_FIELDS}
  ${TICKET_DETAIL_FIELDS}
  query Ticket($id: String!) {
    ticket(id: $id) {
      ...TicketFields
      ...TicketDetailFields
    }
  }
`

export const TICKET_EXTERNAL_CONTACT_QUERY = gql`
  query TicketExternalContact($ticketId: String!) {
    ticketExternalContact(ticketId: $ticketId) {
      normalizedName
      email
      phoneE164
    }
  }
`

/**
 * The cross-tenant L1 queue (ADR-035 KDD-2).
 *
 * A SEPARATE operation name, not a flag on `tickets`, because L1 support works
 * across tenants and the tenant chokepoint exists precisely to pin one tenant
 * per request. The BFF omits tenant_code for this query and ticket-service
 * re-derives the admin tier from the JWT — three independent gates, none of
 * which trusts the one before it. A non-admin is refused server-side, so the
 * client-side role check on the route is an affordance, not the control.
 *
 * Rows carry reporter entities and current container projections so an agent
 * can understand ownership before acting.
 */
export const AGENT_TICKETS_QUERY = gql`
  ${TICKET_FIELDS}
  query SupportAgentTickets(
    $search: String
    $pageSize: Int
    $pageToken: String
  ) {
    supportAgentTickets(
      search: $search
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      tickets {
        ...TicketFields
      }
      nextPageToken
    }
  }
`

export const CREATE_TICKET_MUTATION = gql`
  ${TICKET_FIELDS}
  mutation CreateTicket($input: CreateTicketInput!) {
    createTicket(input: $input) {
      ...TicketFields
    }
  }
`

/**
 * A ticket's conversation, oldest-first.
 *
 * READ THE PAGINATION RULE BEFORE WRITING A LOOP. The server removes comments
 * this caller may not see AFTER reading a page, so a page is caller-dependent
 * in size: 20 stored comments can come back as 15, or as 0, with more behind
 * them. `comments.length < pageSize` therefore means nothing, and an empty
 * `comments` array with a `nextPageToken` is a normal response.
 *
 * The ONLY end-of-thread signal is an absent `nextPageToken`. Stopping on a
 * short page truncates the conversation precisely on threads containing hidden
 * comments — the case where nobody would notice.
 *
 * `redacted` marks a comment the caller may know exists but may not read. Its
 * body and author arrive already cleared by the server. An empty body is NOT
 * the signal — a legitimately short comment looks identical — so branch on the
 * flag and never on the body.
 */
export const TICKET_COMMENTS_QUERY = gql`
  query TicketComments($ticketId: String!, $pageSize: Int, $pageToken: String) {
    ticketComments(
      ticketId: $ticketId
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      comments {
        __typename
        id
        ticketId
        authorUserId
        # Resolved from authorUserId by the BFF when the thread is read. Absent
        # when the directory could not answer, and absent on a withheld comment
        # — the server has already cleared authorUserId there. The client falls
        # back to the id rather than showing nothing.
        authorDisplayName
        scope
        body
        createdAt
        redacted
      }
      nextPageToken
    }
  }
`

export const ADD_TICKET_COMMENT_MUTATION = gql`
  mutation AddTicketComment(
    $ticketId: String!
    $scope: TicketCommentScope!
    $body: String!
  ) {
    addTicketComment(ticketId: $ticketId, scope: $scope, body: $body) {
      __typename
      id
      ticketId
      authorUserId
      scope
      body
      createdAt
      redacted
    }
  }
`

/** `version` is the optimistic-concurrency token and must come from the ticket
 *  being displayed. A stale value is rejected server-side rather than
 *  overwriting a concurrent change — that rejection is the lost-update guard,
 *  so surface it to the user rather than retrying silently. */
/**
 * Move a ticket to another state, optionally recording why it was resolved.
 *
 * The resolution arguments are OPTIONAL because this one mutation drives every
 * transition and ticket-service rejects a resolution on any target but
 * RESOLVED. They are also not decoration: resolving without a code fails with
 * "resolving a ticket requires a resolution code", which is what the Resolve
 * button did on every ticket until the BFF started forwarding these.
 */
export const TRANSITION_TICKET_MUTATION = gql`
  ${TICKET_FIELDS}
  mutation TransitionTicket(
    $id: String!
    $to: TicketState!
    $version: Int!
    $resolutionCode: TicketResolutionCode
    $resolutionNotes: String
  ) {
    transitionTicket(
      id: $id
      to: $to
      version: $version
      resolutionCode: $resolutionCode
      resolutionNotes: $resolutionNotes
    ) {
      ...TicketFields
    }
  }
`

export const ASSIGN_TICKET_MUTATION = gql`
  ${TICKET_FIELDS}
  mutation AssignTicket(
    $id: String!
    $assigneeUserId: String
    $toAdotelAdmin: Boolean
    $version: Int!
  ) {
    assignTicket(
      id: $id
      assigneeUserId: $assigneeUserId
      toAdotelAdmin: $toAdotelAdmin
      version: $version
    ) {
      ...TicketFields
    }
  }
`

// No `version` argument, unlike transition/assign: watchers are not part of the
// ticket's optimistic-concurrency surface, so two agents adding themselves at
// once do not conflict. Both mutations return the full ticket so the mounted
// TICKET_QUERY updates through cache normalisation without a second round trip.
export const ADD_TICKET_WATCHER_MUTATION = gql`
  ${TICKET_FIELDS}
  mutation AddTicketWatcher($ticketId: String!, $userId: String!) {
    addTicketWatcher(ticketId: $ticketId, userId: $userId) {
      ...TicketFields
    }
  }
`

export const REMOVE_TICKET_WATCHER_MUTATION = gql`
  ${TICKET_FIELDS}
  mutation RemoveTicketWatcher($ticketId: String!, $userId: String!) {
    removeTicketWatcher(ticketId: $ticketId, userId: $userId) {
      ...TicketFields
    }
  }
`

export const TICKET_TRACE_LOGS_QUERY = gql`
  query TicketTraceLogs(
    $ticketId: String!
    $pageSize: Int
    $pageToken: String
  ) {
    ticketTraceLogs(
      ticketId: $ticketId
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      context {
        id
        status
        snapshotVersion
        capturedAt
        services
      }
      # userIP and userId are deliberately NOT selected. Nothing renders them,
      # and requesting them would put per-user PII on the wire and into the
      # Apollo cache (and so into devtools) for no display purpose. Add them
      # back only alongside a consumer that actually shows them.
      logs {
        date
        service
        endpointName
        endpointURL
        status
        request
        response
      }
      nextPageToken
    }
  }
`

export const REFRESH_TICKET_TRACE_CONTEXT_MUTATION = gql`
  mutation RefreshTicketTraceContext($ticketId: String!) {
    refreshTicketTraceContext(ticketId: $ticketId) {
      id
      status
      snapshotVersion
      capturedAt
      services
    }
  }
`

// SLA policy administration (BL-034). Adotel-staff only: ticket-service
// requires an admin claim on all four, reads included.
export const TICKET_SLA_POLICY_FIELDS = gql`
  fragment TicketSlaPolicyFields on TicketSlaPolicy {
    __typename
    policyId
    version
    reporterEntityCode
    reporterEntityType
    productScope
    priority
    intakeChannel
    respondAfterSeconds
    resolveAfterSeconds
    enabled
    createdAt
    retiredAt
  }
`

export const TICKET_SLA_POLICIES_QUERY = gql`
  ${TICKET_SLA_POLICY_FIELDS}
  query TicketSlaPolicies(
    $reporterEntityCode: String
    $includeRetired: Boolean
  ) {
    ticketSlaPolicies(
      reporterEntityCode: $reporterEntityCode
      includeRetired: $includeRetired
    ) {
      ...TicketSlaPolicyFields
    }
  }
`

// expectedVersion is the optimistic-concurrency token: the version being
// edited, or 0 to assert this policy has no active version yet (a create).
export const UPSERT_TICKET_SLA_POLICY_MUTATION = gql`
  ${TICKET_SLA_POLICY_FIELDS}
  mutation UpsertTicketSlaPolicy(
    $policyId: String!
    $reporterEntityCode: String!
    $reporterEntityType: String!
    $priority: String!
    $respondAfterSeconds: Int!
    $resolveAfterSeconds: Int!
    $expectedVersion: Int!
  ) {
    upsertTicketSlaPolicy(
      policyId: $policyId
      reporterEntityCode: $reporterEntityCode
      reporterEntityType: $reporterEntityType
      priority: $priority
      respondAfterSeconds: $respondAfterSeconds
      resolveAfterSeconds: $resolveAfterSeconds
      expectedVersion: $expectedVersion
    ) {
      ...TicketSlaPolicyFields
    }
  }
`

export const RETIRE_TICKET_SLA_POLICY_MUTATION = gql`
  ${TICKET_SLA_POLICY_FIELDS}
  mutation RetireTicketSlaPolicy($policyId: String!, $expectedVersion: Int!) {
    retireTicketSlaPolicy(
      policyId: $policyId
      expectedVersion: $expectedVersion
    ) {
      ...TicketSlaPolicyFields
    }
  }
`

// Resolves which policy governs a given ticket shape, using the server's own
// precedence. Exists so no client re-derives that ordering — precedence here
// fails silently, returning a plausible policy rather than an error.
export const RESOLVE_TICKET_SLA_POLICY_QUERY = gql`
  ${TICKET_SLA_POLICY_FIELDS}
  query ResolveTicketSlaPolicy(
    $reporterEntityCode: String!
    $reporterEntityType: String!
    $priority: String!
    $productScope: String
    $intakeChannel: String
  ) {
    resolveTicketSlaPolicy(
      reporterEntityCode: $reporterEntityCode
      reporterEntityType: $reporterEntityType
      priority: $priority
      productScope: $productScope
      intakeChannel: $intakeChannel
    ) {
      ...TicketSlaPolicyFields
    }
  }
`

/**
 * The ticket's audit trail, read as an activity timeline.
 *
 * A deliberately NARROW selection of the same `auditLogs` query the monitoring
 * screen uses. That screen's document walks four levels of nested entities;
 * none of it is needed to render "who did what, when", and GraphQL charges for
 * what you select. `entityId` is the ticket's UUID — every ticket mutation
 * attaches it via the BFF's ticketAuditEntity.
 *
 * `from`/`to` are `YYYY-MM-DD` gregorian strings; the BFF parses them with that
 * exact layout and errors on anything else.
 */
export const TICKET_ACTIVITY_QUERY = gql`
  query TicketActivity($input: AuditLogFiltersInput!) {
    auditLogs(input: $input) {
      totalNumberOfLogs
      logs {
        action
        occurredAt
        level
        user {
          email
          role
        }
        logContent {
          data
        }
      }
    }
  }
`

/**
 * Bulk assignment (BL-235).
 *
 * `version` per item, from the ticket the list already holds — it is the
 * optimistic-concurrency token, so a stale one must fail its own row rather
 * than the batch. The mutation itself succeeds whenever the request was
 * well-formed; partial application is read from the per-item results.
 *
 * There is no "assign to <user>" variant deliberately: the panel's session
 * carries no user id, so CALLER is resolved server-side from the IAM claim.
 */
export const BULK_ASSIGN_TICKETS_MUTATION = gql`
  mutation BulkAssignTickets(
    $items: [TicketBulkAssignItem!]!
    $target: TicketBulkAssignTarget!
  ) {
    bulkAssignTickets(items: $items, target: $target) {
      ticketId
      ok
      code
    }
  }
`

/**
 * Which of these users may actually be added as watchers on this ticket.
 *
 * The picker cannot work this out. Eligibility is "admin, or a named
 * participant, or holding a live grant on the ticket's entity", and a grant
 * lives in the user's IAM `resources` array — which the directory query does
 * not return. Filtering on the one `entityCode` it DOES return would hide
 * anyone homed elsewhere who holds a grant, and `is_admin` is hardcoded false
 * in every list response, so it would also hide every admin.
 *
 * CAPPED AT 100 SERVER-SIDE, and rejected rather than truncated — so a caller
 * that sends more gets an error instead of a quietly short answer. The
 * directory query is unpaginated, so callers must chunk (see useEligible-
 * Watchers).
 */
export const ELIGIBLE_TICKET_WATCHERS_QUERY = gql`
  query EligibleTicketWatchers($ticketId: String!, $userIds: [String!]!) {
    eligibleTicketWatchers(ticketId: $ticketId, userIds: $userIds)
  }
`
